import { z } from 'zod'
import { PaymentMethod } from '../payments/payments'

export enum MovementType {
  Credit = 'credit',
  Debit = 'debit',
}

export enum MovementStatus {
  Pending = 'pending',
  Completed = 'completed',
  Cancelled = 'cancelled',
  Failed = 'failed',
}

export interface MovementChartData {
  movementDate: Date
  amount: number
  type: MovementType
  balanceAfter: number
}

export interface Movement {
  id: string
  accountId: string
  type: MovementType
  amount: number
  balanceAfter: number
  description: string
  status: MovementStatus
  movementDate: Date
  createdBy?: string
  createdAt: Date
  updatedAt: Date
}

export interface CashBoxMovement {
  id: string
  movementType: MovementType
  amount: number
  balanceAfter: number
  description: string
  status: MovementStatus
  movementDate: Date
  paymentId?: string | null
  paymentMethod: PaymentMethod
  accountId: string
  accountNumber?: string
  accountAlias?: string
  customerCode?: string
  customerName?: string | null
  customerId?: string | null
  contractId?: string | null
  contractNumber?: string | null
  createdBy: string
  createdAt: Date
  updatedAt: Date
}

export interface Account {
  id: string
  userId: string
  userName: string
  contractId: string | null
  accountNumber: string
  accountAlias: string
  isMain: boolean
  isCollector: boolean
  status: AccountStatus
  currentBalance: number
  currency: CurrencyType
  lastMovementDate: string | null
  createdAt: string
  updatedAt: string
}

export enum AccountStatus {
  ACTIVE = 'active',
  SUSPENDED = 'suspended'
}

export enum BalanceOperator {
  GREATER_THAN = 'greater_than',
  LESS_THAN = 'less_than',
  EQUAL_TO = 'equal_to',
}

export enum CurrencyType {
  ARS = 'ARS',
  USD = 'USD',
}

export interface AccountSummary {
  id: string;
  userId: string;
  contractId: string | null;
  accountNumber: string;
  isMain: boolean;
  status: AccountStatus;
  currentBalance: number;
  currency: CurrencyType;
  movements: Movement[];
}

export interface CreateMovementData {
  accountId: string
  type: MovementType
  amount: number
  description: string
  reference?: string
  date?: string
}

export interface UpdateMovementData {
  description?: string
  reference?: string
  status?: MovementStatus
}

export interface CashBoxBalance {
  totalDebits: number
  totalCredits: number
  balance: number
  movements: CashBoxMovement[]
}

export interface AccountFilters {
  search?: string;
  userId?: string;
  contractId?: string;
  status?: AccountStatus;
  isMain?: boolean;
  isCollector?: boolean;
  balanceOperator?: BalanceOperator;
  balanceAmount?: number;
  page?: number;
  limit?: number;
}

export interface MovementFilters {
  accountId?: string
  type?: MovementType
  status?: MovementStatus
  startDate?: Date
  endDate?: Date
  description?: string
  page?: number
  limit?: number
}

// Zod validation schemas
export const createMovementSchema = z.object({
  accountId: z.string().min(1, 'La cuenta es requerida'),
  type: z.enum(Object.values(MovementType)),
  amount: z.number().positive('El monto debe ser positivo'),
  description: z.string().min(1, 'La descripción es requerida'),
  reference: z.string().optional(),
  date: z.string().optional(),
})

export const updateMovementSchema = z.object({
  description: z.string().min(1, 'La descripción es requerida').optional(),
  reference: z.string().optional(),
  status: z.enum(Object.values(MovementStatus)).optional(),
})

// Helper functions
export const formatMovementType = (type: MovementType): string => {
  const types: Record<MovementType, string> = {
    [MovementType.Credit]: 'Crédito',
    [MovementType.Debit]: 'Débito',
    // [MovementType.Adjustment]: 'Ajuste',
    // [MovementType.Payment]: 'Pago',
    // [MovementType.Invoice]: 'Factura',
    // [MovementType.Fee]: 'Cargo',
    // [MovementType.Refund]: 'Reembolso',
  }
  return types[type] || type
}

export const formatMovementStatus = (status: MovementStatus): string => {
  const statuses: Record<MovementStatus, string> = {
    [MovementStatus.Pending]: 'Pendiente',
    [MovementStatus.Completed]: 'Completado',
    [MovementStatus.Cancelled]: 'Cancelado',
    [MovementStatus.Failed]: 'Fallido',
  }
  return statuses[status] || status
}

export const getMovementTypeColor = (type: MovementType): string => {
  switch (type) {
    case MovementType.Credit:
      // case MovementType.Payment:
      // case MovementType.Refund:
      return 'text-green-600'
    case MovementType.Debit:
      // case MovementType.Invoice:
      // case MovementType.Fee:
      return 'text-red-600'
    // case MovementType.Adjustment:
    // return 'text-blue-600'
    default:
      return 'text-gray-600'
  }
}

export const isCredit = (type: MovementType): boolean => {
  return [MovementType.Credit].includes(type)
}

export const isDebit = (type: MovementType): boolean => {
  return [MovementType.Debit].includes(type)
}

export const formatAccountStatus = (status: AccountStatus): string => {
  const statuses: Record<AccountStatus, string> = {
    [AccountStatus.ACTIVE]: 'Activa',
    [AccountStatus.SUSPENDED]: 'Suspendida',
  }
  return statuses[status] || status
}

export const getAccountStatusColor = (status: AccountStatus): string => {
  switch (status) {
    case AccountStatus.ACTIVE:
      return 'border-green-600 text-green-600 bg-green-50 hover:bg-green-50'
    case AccountStatus.SUSPENDED:
      return 'border-red-600 text-red-600 bg-red-50 hover:bg-red-50'
    default:
      return 'border-gray-600 text-gray-600 hover:bg-gray-50'
  }
}

export const editAccountSchema = z.object({
  accountAlias: z.string().optional(),
  status: z.enum([AccountStatus.ACTIVE, AccountStatus.SUSPENDED]).optional(),
})

export type EditAccountValues = z.infer<typeof editAccountSchema>

export const movementFormSchema = z.object({
  amount: z.number().positive('El monto debe ser positivo'),
  description: z.string().min(1, 'El concepto es obligatorio'),
  paymentMethod: z.enum(PaymentMethod),
  observations: z.string().optional(),
  movementDate: z.string().optional(),
  paymentVerified: z.boolean(),
  hasInvoice: z.boolean(),
  invoiceNumber: z.string(),
  taxRate: z.number().min(0).max(100).optional(),
  subtotal: z.number().positive('El subtotal debe ser positivo').optional(),
  totalAmount: z.number().positive('El total debe ser positivo').optional(),
}).superRefine((data, ctx) => {
  if (data.hasInvoice && (!data.invoiceNumber || data.invoiceNumber.trim() === '')) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceNumber'],
      message: 'El número de factura es obligatorio cuando tiene factura',
    });
  }
});

export type MovementFormValues = z.infer<typeof movementFormSchema>